import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import React, { useCallback, useContext, useEffect } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { NewHireStep } from './models/new-hire-model';
import { useNewHireState } from './hooks/useNewHireState';
import { InstitutionState } from '../institutions/institution-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { setIsReview, setNewHireStep } from './new-hire-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { ClergyNewHireFormValues, EmploymentType, NewHireFormValues } from './models/new-hire-form-model';
import { NewHireForm } from './components/new-hire-form';
import { useEffectUpdate } from '../../../../global/hooks/useEffectUpdate';
import { PersonSearchContext, PersonSearchKeys, PersonSearchState } from '../person-search/person-search-model';
import { dispatchSetCurrentEmployee, dispatchSetEmployment, dispatchSetEmploymentFailed, dispatchSetNonStipendiaryEmployment, removeNonStipEmployment, removeStipEmployment, setCurrentEmployee } from '../employment/employment-slice';
import { Employee, EmploymentState } from '../employment/employment-model';
import { useNewHireSettings } from './hooks/useNewHireSettings';
import { FormTypes, matchedPersonField } from '../../../account-info/models/form-models';
import { Person } from '../../../../global/models/person-model';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { BaseApiState, YesNoIndicators } from '../../../../global/models/api-model';
import { OSCRRService } from '../person-search/oscrr-helpers';
import { NewHireState } from './models/new-hire-state-model';
import { setHideToast, setSearchResults } from '../person-search/person-search-slice';
import { getEmployment, getEmployments, getNonStipendiaryEmployment } from '../../../../global/ducks/benadmin-api';
import { setPersonWithHandler } from '../../../../global/ducks/person-prc-api';
import { dispatchSetCurrentPerson, dispatchSetCurrentPersonFailed } from '../../../account-info/features/personal-info/personal-info-slice';
import { GetEmploymentsAndCompensations } from '../employment-compensation/employment-compensation-requests';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { setCurrentNewHireEnrollmentStep } from '../new-hire-enrollment/slice/new-hire-enrollment-slices';
import { NewHireEnrollmentStep } from '../new-hire-enrollment/models/new-hire-enrollment-models';
import { NewHireSubmitAction } from './new-hire-helper';

interface Props {
	settingsId: string;
	nonStip: boolean;
	succesRedirectOverwrite?: string;
}

export function NewHireApp(props: Props) {
	const groupName = matchedPersonField;
	const { settingsId, nonStip, succesRedirectOverwrite } = props;

	const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const newHireState = useSelector<TcsmStates, NewHireState>(state => state.newHireState);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const labelSettings = epiProps?.labelSettingState?.epiContent;
	const newHireProps = epiProps?.newHireSettingsState?.epiContent;
	const settingsLoaded = useNewHireSettings(settingsId);
	const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`);
	const employmentUrl = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Employment}`);
	const successUrl = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.NewHireConfirmation}`);
	const nonTecNonLicenseUrl = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.NonTecNonLicenseNewClergy}`);

	const routeMatch = useRouteMatch();
	const currentStep = newHireState?.currentStep;
	const currentEnrollmentStep = useSelector<TcsmStates, NewHireEnrollmentStep>(state => state?.newHireEnrollmentState?.currentStep);
	const currentPerson = useSelector<TcsmStates, Person>(state => state.personalInfo[DemographicAppKeys.arc]?.currentPerson);
	const personSearchKey = PersonSearchKeys.NewHire;
	const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state?.employmentState);

	const enrollmentContext = useContext(AnnualEnrollmentContext);

	useNewHireState();
	const history = useHistory();
	const dispatch = useDispatch();

	// smooth scroll to top of page whenever the current step changes
	const stepChangeCallback = useCallback(() =>
		setTimeout(() => window.scrollTo(
			{
				top: 0,
				left: 0,
				behavior: 'smooth',
			}), 10),
		[currentStep]);
	useEffectUpdate(stepChangeCallback);

	useEffect(() => {
		dispatch(apiErrorClear());

		if (enrollmentContext === AnnualEnrollmentKey.newHireEnrollment && !currentEnrollmentStep) {
			dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.EMPLOYEE_RECORD));
		}
	}, []);

	useEffect(() => {
		if (!newHireState?.isReview && currentPerson) {
			dispatch(setNewHireStep(NewHireStep.PersonReviewExternal));
		}

		if (newHireState?.isReview && employmentState?.currentEmployee?.id) {
			const employeeId = employmentState?.currentEmployee?.id;
			const employmentId = employmentState?.currentEmployee?.employment_id;
			const institutionClientNumber = institutionState?.institutionClientNumber;

			if (!currentPerson) {
				dispatch(setPersonWithHandler(employmentState?.currentEmployee?.guid, dispatchSetCurrentPerson(dispatch, DemographicAppKeys.arc), () => dispatchSetCurrentPersonFailed(dispatch, DemographicAppKeys.arc)(true)));
			}

			if (employmentId) {
				dispatch(getEmployment(employeeId, employmentId, dispatchSetEmployment(dispatch), () => dispatchSetEmploymentFailed(dispatch)));
			}

			if (nonStip) {
				dispatch(getNonStipendiaryEmployment(employeeId, dispatchSetNonStipendiaryEmployment(dispatch, institutionClientNumber)));
			}

			if (!nonStip) {
				dispatch(getEmployments(
					employmentState?.currentEmployee?.guid,
					(response) => GetEmploymentsAndCompensations(dispatch, response),
				));
			}
		}
	}, [currentPerson?.person_guid]);

	if (!institutionState?.institutionClientNumber) {
		return (
			<Redirect to=
				{
					{
						pathname: institutionSelect,
						state: {
							actionLink: routeMatch.path,
						},
					}
				}
			/>);
	}

	if (!settingsLoaded ||
		(newHireState?.isReview && employmentState?.currentEmployee?.guid && !currentPerson)) {
		return <LoadingCircle />;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	function OnCancelReview(formProps: FormikProps<NewHireFormValues>, employmentType?: boolean, nonStipEmploymentBack?: boolean) {
		if (currentStep === NewHireStep.EmploymentCompensationClergy) {
			dispatch(setNewHireStep(NewHireStep.PersonalInfo));
			return;
		}

		if (currentStep === NewHireStep.EmploymentCompensationLay) {
			dispatch(setNewHireStep(NewHireStep.PersonalInfo));
			return;
		}

		formProps.resetForm();
	}

	const onCancel = (formProps: FormikProps<NewHireFormValues>, employmentType?: boolean, nonStipEmploymentBack?: boolean) => {
		dispatch(setSearchResults([], personSearchKey));
		dispatch(setHideToast(false, personSearchKey));

		if (newHireState?.isReview) {
			OnCancelReview(formProps, employmentType, nonStipEmploymentBack);
			return;
		}

		if (nonStipEmploymentBack) {
			formProps.setFieldValue(groupName, null);
			dispatch(setNewHireStep(NewHireStep.EmploymentType));
			return;
		}

		if (employmentType) {
			dispatch(setNewHireStep(NewHireStep.PersonalInfo));
			return;
		}

		if (currentStep === NewHireStep.ClergyType) {
			dispatch(setNewHireStep(NewHireStep.EmploymentType));
			return;
		}

		if (currentStep === NewHireStep.EmploymentCompensationClergy) {
			dispatch(setNewHireStep(NewHireStep.ClergyType));
			return;
		}

		if (currentStep === NewHireStep.EmploymentCompensationLay) {
			dispatch(setNewHireStep(NewHireStep.EmploymentType));
			return;
		}

		if (currentStep === NewHireStep.PersonalInfo) {
			formProps.resetForm();
		}

		if (currentStep === NewHireStep.HireDetails
			|| currentStep === NewHireStep.PersonReviewExternal
			|| currentStep === NewHireStep.PersonReviewInternal
			|| currentStep === NewHireStep.EmploymentType) {
			dispatch(setNewHireStep(NewHireStep.PersonalInfo));
		}
	};

	function NavigateToSucessUrl(formType: FormTypes) {
		dispatch(removeNonStipEmployment());
		dispatch(removeStipEmployment());
		dispatch(setIsReview(true));
		history.push(`${succesRedirectOverwrite || successUrl}?success=${formType}`);
	}

	const onSubmitSuccess = (noStip: boolean, employee: any, formValues: ClergyNewHireFormValues) => {
		const temp = {
			...employee,
			guid: employee?.employee_guid,
			id: employee?.employee_guid,
			masked_client_number: employee?.masked_employee_client_number,
			employment_id: employee?.employment_id,
			full_name: employee?.employee_name,
		};

		let formType = noStip ? FormTypes.NON_STIP_NEW_HIRE : FormTypes.STIP_NEW_HIRE;
		if (formValues?.employment_type === EmploymentType.Clergy && formValues?.is_TEC === YesNoIndicators.Yes) {
			formType = FormTypes.NEW_CLERGY_ENROLLED;
		}
		if (formValues?.employment_type === EmploymentType.Clergy
			&& formValues?.is_TEC === YesNoIndicators.No
			&& formValues?.is_another_denomination === YesNoIndicators.Yes
		) {
			formType = FormTypes.NEW_CLERGY_ENROLLED;
		}
		dispatchSetCurrentEmployee(dispatch, temp);
		NavigateToSucessUrl(formType);
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	function OnSubmitReview(values: NewHireFormValues, formikHelpers: FormikHelpers<NewHireFormValues>, selectedPersonGuid: string, internalEmployee: Employee) {
		const clergyNewHireFormValues = values as ClergyNewHireFormValues;

		if (currentStep === NewHireStep.PersonalInfo) {

			if (clergyNewHireFormValues?.employment_type === EmploymentType.Clergy) {
				dispatch(setNewHireStep(NewHireStep.EmploymentCompensationClergy));
			}

			dispatch(setNewHireStep(NewHireStep.EmploymentCompensationLay));
		}

		if (currentStep === NewHireStep.EmploymentCompensationClergy ||
			currentStep === NewHireStep.EmploymentCompensationLay) {
			const formType = nonStip ? FormTypes.NON_STIP_NEW_HIRE : FormTypes.STIP_NEW_HIRE;

			NavigateToSucessUrl(formType);
		}
	}

	async function onSubmit(values: NewHireFormValues, formikHelpers: FormikHelpers<NewHireFormValues>, selectedPersonGuid: string, internalEmployee: Employee) {

		if (newHireState?.isReview) {
			OnSubmitReview(values, formikHelpers, selectedPersonGuid, internalEmployee);
			return;
		}

		const clergyNewHireFormValues = values as ClergyNewHireFormValues;

		switch (currentStep) {
			case NewHireStep.PersonalInfo:
				// Check if new results were found after click.
				if (await OSCRRService.SearchHasNewResultsAsync(apiState, personSearchState, values[groupName])) return;

				dispatch(apiErrorClear());
				formikHelpers.setTouched({});
				formikHelpers.setSubmitting(false);
				dispatch(setNewHireStep(NewHireStep.EmploymentType));
				break;

			case NewHireStep.PersonReviewExternal:

				dispatch(apiErrorClear());
				formikHelpers.setTouched({});
				formikHelpers.setSubmitting(false);
				dispatch(setNewHireStep(NewHireStep.HireDetails));
				break;

			case NewHireStep.PersonReviewInternal:

				dispatch(setCurrentEmployee(internalEmployee));
				history.push(employmentUrl);
				break;

			case NewHireStep.EmploymentType:

				dispatch(apiErrorClear());
				formikHelpers.setTouched({});
				formikHelpers.setSubmitting(false);

				if (!nonStip && clergyNewHireFormValues?.employment_type === EmploymentType.Clergy) {
					dispatch(setNewHireStep(NewHireStep.ClergyType));
					return;
				}

				if (nonStip) {
					dispatch(setNewHireStep(NewHireStep.HireDetails));
				}

				dispatch(setNewHireStep(NewHireStep.EmploymentCompensationLay));
				break;

			case NewHireStep.ClergyType:

				dispatch(apiErrorClear());
				formikHelpers.setTouched({});
				formikHelpers.setSubmitting(false);
				dispatch(setNewHireStep(NewHireStep.EmploymentCompensationClergy));
				if (clergyNewHireFormValues?.employment_type === EmploymentType.Clergy
					&& clergyNewHireFormValues?.is_TEC === YesNoIndicators.No
					&& clergyNewHireFormValues?.is_another_denomination === YesNoIndicators.No
				)
					history.push(nonTecNonLicenseUrl);

				break;

			case NewHireStep.HireDetails:
				NewHireSubmitAction(
					dispatch,
					selectedPersonGuid,
					values,
					nonStip,
					institutionState?.institutionGuid,
					onSubmitSuccess,
					institutionState?.institutionClientNumber,
				);
				break;

			case NewHireStep.EmploymentCompensationClergy:

				NewHireSubmitAction(
					dispatch,
					selectedPersonGuid,
					values,
					nonStip,
					institutionState?.institutionGuid,
					onSubmitSuccess,
					institutionState?.institutionClientNumber,
				);
				break;

			case NewHireStep.EmploymentCompensationLay:

				NewHireSubmitAction(
					dispatch,
					selectedPersonGuid,
					values,
					nonStip,
					institutionState?.institutionGuid,
					onSubmitSuccess,
					institutionState?.institutionClientNumber,
				);
				break;

			default:
				break;
		}


	}

	return (
		<PersonSearchContext.Provider value={PersonSearchKeys.NewHire}>
			{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}

			<NewHireForm
				nonStip={nonStip}
				currentStep={currentStep}
				labelSettings={labelSettings}
				newHireProps={newHireProps}
				onCancel={onCancel}
				onSubmit={onSubmit}
				currentPerson={currentPerson}
				isReview={newHireState?.isReview}
			/>
		</PersonSearchContext.Provider>
	);
}

NewHireApp.defaultProps = {
	succesRedirectOverwrite: null,
};